import { BufferGeometry, Material, Mesh } from "three";
import { useEffect, useMemo, useRef } from "react";
import { GONDOLA_COLORS } from "@/features/sku-sections/lib/polygon-colors";
import { Annotation } from "@/components/annotation";
import { useSelector } from "@legendapp/state/react";
import { useViewSettings } from "@/view-settings";

export function PlanogramMesh({
  vertices,
  faces,
  label,
  ...props
}: JSX.IntrinsicElements["mesh"] & {
  vertices: number[];
  faces: number[];
  label: string;
}) {
  const mesh = useRef<Mesh<BufferGeometry, Material | Material[]> | null>(null);
  const geometry = useRef<BufferGeometry | null>(null);
  const verticesArray = useMemo(() => new Float32Array(vertices), [vertices]);
  const viewSettings = useViewSettings();
  const showLabels = useSelector(() => viewSettings.showPlanogramLabels.get());

  useEffect(() => {
    if (geometry.current && faces) {
      geometry.current.setIndex(faces);
    }
  }, [faces]);

  const fillColor = GONDOLA_COLORS.DEFAULT;
  const labelCenter = useMemo(() => {
    let minX = Infinity,
      maxX = -Infinity;
    let minY = Infinity,
      maxY = -Infinity;
    let minZ = Infinity,
      maxZ = -Infinity;

    for (let i = 0; i < verticesArray.length; i += 3) {
      minX = Math.min(minX, verticesArray[i]);
      maxX = Math.max(maxX, verticesArray[i]);
      minY = Math.min(minY, verticesArray[i + 1]);
      maxY = Math.max(maxY, verticesArray[i + 1]);
      minZ = Math.min(minZ, verticesArray[i + 2]);
      maxZ = Math.max(maxZ, verticesArray[i + 2]);
    }

    return [(minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2] as const;
  }, [verticesArray]);

  return (
    <group>
      {showLabels && (
        <Annotation
          wrapperClassName="pointer-events-auto"
          position={labelCenter}
          zIndexRange={[100, 0]}
        >
          <span className="text-xs whitespace-nowrap font-bold">{label}</span>
        </Annotation>
      )}

      <mesh {...props} ref={mesh}>
        <bufferGeometry ref={geometry}>
          <bufferAttribute
            attach="attributes-position"
            count={verticesArray.length / 3}
            array={verticesArray}
            itemSize={3}
            normalized={false}
          />
        </bufferGeometry>
        <meshBasicMaterial color={fillColor} opacity={0.3} transparent />
      </mesh>
    </group>
  );
}
